import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { CustomIcon } from 'components/CustomIcon';
import { InformationIcon } from 'icons';
import palette from 'theme/palette';
import {
  FilterButton,
  FilterLabel,
  FilterAction,
  SwitchBox,
  SwitchLabel,
  TooltipWrapper,
  ItemNumber,
  Badge,
  FilterLabelActions,
} from './FilterOption.styles';
import MBCheckbox from '../../Elements/MBCheckbox';
import MBSwitch from '../../Elements/MBSwitch/MBSwitch';
import { Typography } from '../../Typography';
import { FilterItemOptionProps } from '../Filters.types';

import { SwitchTooltip, BootstrapTooltip } from './Tooltips';

const SwitchInfoTooltip: React.FC<{ switchTooltipContent: string }> = ({
  switchTooltipContent,
}) => {
  return (
    <SwitchTooltip
      title={
        <React.Fragment>
          <TooltipWrapper>
            <Typography fontSize="16px" lineHeight="1.5">
              {switchTooltipContent}
            </Typography>
          </TooltipWrapper>
        </React.Fragment>
      }
      placement="bottom"
    >
      <span>
        <InformationIcon />
      </span>
    </SwitchTooltip>
  );
};

const FilterOption = React.memo(
  ({
    isLoading,
    onClick,
    isSelected,
    item,
    label,
    count,
    disabled,
    isChild,
    checkboxVariant,
    rightIcon,
    onCheckboxClick,
    onLabelClick,
    isSwitch = false,
    marginBottom = 18,
    buttonId = undefined,
    badgeNumber,
    customSwitchClass,
    children,
    switchTooltipContent,
  }: FilterItemOptionProps) => {
    const textLabel = label || item?.label;
    const itemCount = count || item?.count;
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleClick = (event: React.SyntheticEvent) => {
      onClick?.(item, event);
    };

    const handleOnCheckboxClick = (event: React.SyntheticEvent) => {
      onCheckboxClick?.(item, event);
    };

    const handleOnLabelClick = (event: React.SyntheticEvent) => {
      onLabelClick?.(item, event);
    };

    const handleClose = () => {
      setTooltipOpen(false);
    };

    const handleOpen = (e: any) => {
      setTooltipOpen(e.target?.offsetWidth < e.target?.scrollWidth);
    };

    if (isSwitch)
      return (
        <SwitchBox
          className={customSwitchClass}
          id={buttonId}
          disabled={disabled}
          marginBottom={marginBottom}
          onClick={handleClick}
        >
          <SwitchLabel fontWeight={400} color={palette.primary.main}>
            {children || textLabel}
            {switchTooltipContent && (
              <SwitchInfoTooltip switchTooltipContent={switchTooltipContent} />
            )}
          </SwitchLabel>
          {isLoading ? <CircularProgress /> : <MBSwitch checked={isSelected} />}
        </SwitchBox>
      );

    return (
      <BootstrapTooltip
        open={tooltipOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        title={textLabel}
        placement="right"
      >
        <FilterButton
          id={buttonId}
          onClick={handleClick}
          disabled={disabled}
          ischild={isChild}
          marginBottom={marginBottom}
        >
          <FilterAction isSelected={isSelected}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <MBCheckbox
                onClick={handleOnCheckboxClick}
                variant={checkboxVariant}
                hovered
                checked={isSelected}
                backgroundColor={palette.grey[300]}
              />
            )}
          </FilterAction>
          <FilterLabel onClick={handleOnLabelClick}>
            <Typography textAlign="left" className="filterName">
              {children || textLabel}
            </Typography>
            <FilterLabelActions>
              {(badgeNumber ?? 0) > 0 && <Badge>{badgeNumber}</Badge>}
              {itemCount != null && (
                <ItemNumber variant="micro" pl={1}>
                  {itemCount}
                </ItemNumber>
              )}
              {rightIcon && <CustomIcon name={rightIcon} dense size={0.8} />}
            </FilterLabelActions>
          </FilterLabel>
        </FilterButton>
      </BootstrapTooltip>
    );
  },
  isEqual,
);

FilterOption.displayName = 'FilterOption';

export default FilterOption;
